import {
  BundleDownloadStatusType,
  TemplateType,
  useBundleRequestDownloadMutation,
  useGetTemplateListQuery,
  useLazyBundleDownloadStatusQuery,
  useLazyGetBundleGetDownloadQuery,
  useLazyGetBundleGetDownloadIndexQuery,
} from '@/api';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  BundleType,
  BundleTypeKeys,
  selectBundleDownloadStatusByStoreKey,
  selectBundleSections,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  setBundleDownloadStatus,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { format } from 'date-fns';
import { useAzureFileUpload } from '@repo/file-uploader';
import { useGlobalToast } from '@repo/atomic-components';
import { downloadFilesAsZip } from '@/ui/pages/Case/CaseItemPage/AiProcessedDocuments/utils/downloadFilesAsZip';

const POLL_INTERVAL_MS = 20 * 1000;

const resetStorageKey = (bundleId: string, bundleType: string) => `bundle_dl_reset_${bundleId}_${bundleType}`;

export const useBundleDownloadButton = () => {
  const { downloadFile } = useAzureFileUpload();
  const dispatch = useAppDispatch();
  const globalToastRef = useGlobalToast();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const { caseId } = useParams<{ caseId: string }>();

  const selectBundleDownloadStatus = useMemo(
    () => selectBundleDownloadStatusByStoreKey(activeBundleType ?? undefined),
    [activeBundleType],
  );
  const bundleDownloadStatus = useAppSelector(selectBundleDownloadStatus);

  const [triggerBundleRequestDownload] = useBundleRequestDownloadMutation();
  const [triggerBundleDownloadStatus] = useLazyBundleDownloadStatusQuery();
  const [triggerBundleGetDownload] = useLazyGetBundleGetDownloadQuery();
  const [triggerBundleGetDownloadIndex] = useLazyGetBundleGetDownloadIndexQuery();

  // Custom INDEX templates for the current org — surfaced as extra choices
  // in the DOCX index picker alongside the default template. Only fetched
  // when the user has permission to export, since the picker is gated
  // behind the same access-control action.
  const isExportBundlesTimelinesChronologies = useAccessControl(
    AccessControlAction.EXPORT_BUNDLES_TIMELINES_CHRONOLOGIES,
  );
  const { data: indexTemplatesData } = useGetTemplateListQuery(
    { templateType: TemplateType.INDEX },
    { skip: !isExportBundlesTimelinesChronologies },
  );
  // Templates need an uploadedAt before they can be used to generate an index.
  const indexTemplates = useMemo(
    () => indexTemplatesData?.templates.filter((template) => template.uploadedAt) ?? [],
    [indexTemplatesData],
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [indexErrorMessage, setIndexErrorMessage] = useState<string | null>(null);

  const pollingActiveRef = useRef(false);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startPolling = useCallback(
    (id: string, bundleKey: BundleTypeKeys) => {
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      pollingActiveRef.current = true;

      const poll = () => {
        if (!pollingActiveRef.current) return;

        triggerBundleDownloadStatus({ bundleId: id, bundleKey })
          .then((response) => {
            if (!pollingActiveRef.current) return;
            const status = response.data?.status;

            if (status === BundleDownloadStatusType.DOWNLOAD_READY) {
              setIsGenerating(false);
              dispatch(setBundleDownloadStatus({ key: bundleKey, data: BundleDownloadStatusType.DOWNLOAD_READY }));
              globalToastRef.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'PDF bundle is ready to download.',
                life: 4000,
              });
              pollingActiveRef.current = false;
              pollingTimeoutRef.current = null;
            } else if (status === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
              setIsGenerating(false);
              setIsError(true);
              setErrorMessage('Bundle Generation failed, please refresh the page and try again');
              dispatch(
                setBundleDownloadStatus({
                  key: bundleKey,
                  data: BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED,
                }),
              );
              globalToastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Bundle Generation failed, please refresh the page and try again',
                life: 5000,
              });
              pollingActiveRef.current = false;
              pollingTimeoutRef.current = null;
            } else if (status === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
              pollingTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            } else {
              setIsGenerating(false);
              pollingActiveRef.current = false;
              pollingTimeoutRef.current = null;
            }
          })
          .catch(() => {
            setIsGenerating(false);
            pollingActiveRef.current = false;
            pollingTimeoutRef.current = null;
          });
      };

      poll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerBundleDownloadStatus, dispatch],
  );

  // On mount: check status once; resume polling if still preparing; restore ready state unless
  // the user explicitly reset it this session (tracked in sessionStorage)
  useEffect(() => {
    if (!bundleId || !activeBundleType) return;

    triggerBundleDownloadStatus({ bundleId, bundleKey: activeBundleType }).then((response) => {
      const status = response.data?.status;
      if (status === BundleDownloadStatusType.DOWNLOAD_PREPARING) {
        setIsGenerating(true);
        pollingActiveRef.current = true;
        pollingTimeoutRef.current = setTimeout(() => startPolling(bundleId, activeBundleType), POLL_INTERVAL_MS);
      } else if (status === BundleDownloadStatusType.DOWNLOAD_READY) {
        const wasReset = sessionStorage.getItem(resetStorageKey(bundleId, activeBundleType));
        if (!wasReset) {
          dispatch(setBundleDownloadStatus({ key: activeBundleType, data: BundleDownloadStatusType.DOWNLOAD_READY }));
        }
      } else if (status === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED) {
        // Reset to idle on page load so user can retry cleanly
        dispatch(setBundleDownloadStatus({ key: activeBundleType, data: BundleDownloadStatusType.IN_PROGRESS }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleId]);

  useEffect(() => {
    return () => {
      pollingActiveRef.current = false;
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const handleGeneratePdf = useCallback(
    (withPageNumbers: boolean) => {
      if (!bundleId || !activeBundleType || isGenerating) return;
      sessionStorage.removeItem(resetStorageKey(bundleId, activeBundleType));
      setIsGenerating(true);
      setIsError(false);
      setErrorMessage(null);
      triggerBundleRequestDownload({ bundleId, withPageNumbers })
        .unwrap()
        .then(() => startPolling(bundleId, activeBundleType))
        .catch((error: { status?: number }) => {
          setIsGenerating(false);
          setIsError(true);
          const message =
            error?.status === 403
              ? "You don't have permission to generate a bundle"
              : 'Bundle Generation failed, please refresh the page and try again';
          setErrorMessage(message);
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
          });
        });
    },
    [bundleId, activeBundleType, isGenerating, triggerBundleRequestDownload, startPolling],
  );

  const handleDownloadPdf = useCallback(() => {
    if (!bundleId || !caseId || !activeBundleType) return;
    if (bundleDownloadStatus !== BundleDownloadStatusType.DOWNLOAD_READY) return;

    const bundleLabel = activeBundleType === BundleType.FULL ? 'full' : 'light';

    triggerBundleGetDownload({ bundleId })
      .unwrap()
      .then((data) => {
        if (data?.bundleDownload) {
          downloadFilesAsZip(
            [data.bundleDownload],
            `${caseId}_${bundleLabel}_bundle_${format(new Date(), 'y_MMM_d_HH_mm')}`,
          );
        }
      })
      .catch(() => {
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to download PDF bundle. Please try again.',
          life: 5000,
        });
      });
  }, [bundleId, caseId, activeBundleType, bundleDownloadStatus, triggerBundleGetDownload, globalToastRef]);

  const handleDownloadIndex = useCallback(
    (templateId?: string) => {
      if (!bundleId || !caseId || !activeBundleType) return;
      setIndexErrorMessage(null);
      const bundleLabel = activeBundleType === BundleType.FULL ? 'full' : 'light';
      triggerBundleGetDownloadIndex({ bundleId, templateId })
        .unwrap()
        .then((data) => {
          if (data?.downloadPath && data?.downloadToken) {
            downloadFile(
              `${caseId}_${bundleLabel}_bundle_index_${format(new Date(), 'y_MMM_d_HH_mm')}`,
              data.downloadPath,
              data.downloadToken,
            );
          }
        })
        .catch((error: { status?: number }) => {
          const message =
            error?.status === 403
              ? "You don't have permission to download the index"
              : 'Failed to download DOCX index. Please refresh the page and try again';
          setIndexErrorMessage(message);
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
          });
        });
    },
    [bundleId, caseId, activeBundleType, triggerBundleGetDownloadIndex, downloadFile, globalToastRef],
  );

  const handleReset = useCallback(() => {
    if (!activeBundleType || !bundleId) return;
    sessionStorage.setItem(resetStorageKey(bundleId, activeBundleType), '1');
    pollingActiveRef.current = false;
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsGenerating(false);
    setIsError(false);
    setErrorMessage(null);
    dispatch(setBundleDownloadStatus({ key: activeBundleType, data: BundleDownloadStatusType.IN_PROGRESS }));
  }, [bundleId, activeBundleType, dispatch]);

  // Auto-reset the generated download whenever the bundle's sections tree
  // changes AFTER generation has finished (page moves, renames,
  // extract/remove etc.) — otherwise the user could download a stale PDF
  // that no longer matches what they see. Deliberately do NOT reset while
  // generation is in progress: doing so would kill the polling / spinner
  // and force the user to start over, so mid-generation edits are allowed
  // to complete against whatever the backend snapshotted on request.
  // Skip the first observation so an existing DOWNLOAD_READY state on
  // mount isn't wiped just because the sections finished loading.
  const bundleSections = useAppSelector(selectBundleSections);
  const previousSectionsRef = useRef(bundleSections);
  useEffect(() => {
    if (previousSectionsRef.current === bundleSections) return;
    const isFirstObservation = previousSectionsRef.current.length === 0;
    previousSectionsRef.current = bundleSections;
    if (isFirstObservation) return;
    if (bundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleSections]);

  return {
    isGenerating,
    isPdfReady: bundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY,
    isError,
    errorMessage,
    indexErrorMessage,
    indexTemplates,
    handleGeneratePdf,
    handleDownloadPdf,
    handleDownloadIndex,
    handleReset,
  };
};
